import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { cpfMask } from "utils/cpf";
import api from "services/api";

import { Box, Button, CircularProgress, FormControl, FormHelperText, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { Container, Cell, ButtonContainer } from "./styled";
import { IPerson } from "./types";
import { useAlert } from "contexts/alert";
import { DeleteOutlined, UploadFile } from "@mui/icons-material";

dayjs.extend(utc)

function formatDate(dateToFormat: string | null) {
  try {
    if (!dateToFormat) throw new Error();
    if (!dayjs(dateToFormat).isValid()) throw new Error();
    
    return dayjs(dateToFormat).utc().format(`DD/MM/YYYY`);
  } catch (error) {
    return `-`
  }
}

export function ExclusionView() {
  const { workspaceId, templateId, workspaceActivityId } = useParams() as { workspaceId: string, templateId: string, workspaceActivityId: string };
  const history = useHistory();
  const { openAlert } = useAlert();
  
  const [people, setPeople] = useState<IPerson[] | null>(null);
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [errors, setErrors] = useState<Record<string, string>[]>([]);

  const { data: originalData, isFetching: isQueryFetching, isFetched: isQueryFetched, isError: isQueryError } = useQuery({
    queryKey: ["adesao", workspaceActivityId],
    queryFn: () => api.get<{ data: IPerson[] }>(`/v2/workspaces/${workspaceId}/templates/${templateId}/exclusion/${workspaceActivityId}`),
    select: (data) => data.data.data,
  });

  const { isSuccess: isMutateSuccess, isPending: isMutatePending, isError: isMutateError, mutate } = useMutation({
    mutationFn: async (payload: IPerson[]) => {
      setErrors([]);

      let totalErrors = [];

      for (const person of payload) {
        const personFiles = Object.entries(files).find(([taxPayerId, files]) => taxPayerId === person?.taxPayerId)?.[1];
        if (!!person.continueWithPlan || !!person.selectedDate || !!person.chosenPlan || !!personFiles) {
          //Se voce chegou a preencher qualquer campo
          let errorsFound = {};
  
          console.log(person.taxPayerId, personFiles)
  
          if (!person?.continueWithPlan) errorsFound = { ...errorsFound, continueWithPlan: "Campo obrigatório" };
          if (!person?.selectedDate) errorsFound = { ...errorsFound, selectedDate: "Campo obrigatório" };
          // if (!personFiles) errorsFound = { ...errorsFound, files: "Selecione ao menos um arquivo" }
  
          totalErrors.push(errorsFound);
        } else {
          totalErrors.push({})
        }
      }

      if (totalErrors.find(e => !!Object?.entries(e)?.length)) {
        setErrors(totalErrors);
        throw new Error("Verifique os campos obrigatórios");
      }

      const peopleFilledOut = payload.filter(person => !!person.continueWithPlan || !!person.selectedDate || !!person.chosenPlan);
      if (!peopleFilledOut.length) throw new Error("Nenhuma alteração identificada");

      await api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/exclusion/${workspaceActivityId}`, peopleFilledOut);

      const uploads = Object.entries(files).map(([taxPayerId, files]) => files.map(file => [taxPayerId, file])).flat();

      for (const [cpf, file] of uploads) {
        const formData = new FormData();
        formData.append('file', file as File);
        formData.append('cpf', cpf);

        try {
          await api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/adesao/${workspaceActivityId}/upload-files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          console.log(`Arquivo de ${cpf} enviado com sucesso.`);
        } catch (err) {
          console.error(`Erro ao enviar arquivo de ${cpf}:`, err);
        }
      }
    },
    onSuccess: () => {
      openAlert('Dados salvos com sucesso', 'success');
      history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/movimentacao`);
    },
    onError: (err) => {
      console.log("[Error @ Exclusion]", err)
      if (err?.message) return openAlert(err.message, 'error');

      openAlert('Algo deu errado ao salvar dados', 'error');
    },
  });

  useEffect(() => {
    if (originalData && isQueryFetched) {
      setPeople(originalData);
    };
  }, [originalData]);

  function handleChangePlan(personCpf: string, plan: string) {
    if (!people) return;

    const copyChanged = [...people].map(person => person.taxPayerId !== personCpf ? person : ({
      ...person,
      chosenPlan: plan
    }));

    //@ts-ignore
    setPeople((prev) => [...copyChanged]);
  };

  function handleChangeOption(personCpf: string, planStatus: string) {
    if (!people) return;

    const copyChanged = [...people].map(person => person.taxPayerId !== personCpf ? person : ({
      ...person,
      continueWithPlan: planStatus === 'SIM' ? 'SIM' : 'NAO'
    }));

    //@ts-ignore
    setPeople((prev) => [...copyChanged]);
  };

  function handleChangeDate(personCpf: string, newDate: string) {
    if (!people) return;

    const copyChanged = [...people].map(person => person.taxPayerId !== personCpf ? person : ({
      ...person,
      selectedDate: newDate,
    }));

    setPeople((prev) => [...copyChanged]);
  };

  function handleCancel() {
    if (originalData) {
      setPeople(originalData);
      setErrors([]);
      // setFiles({});
    }
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>, taxPayerId: string) {
    if (!event?.target?.files?.length) {
      return openAlert('Por favor, selecione ao menos um arquivo válido.', 'error');
    }

    const uploadedFiles = event.target.files;

    for (const file of uploadedFiles) {
      if (!file || (file.type !== "application/pdf" && file.type !== "image/png" && file.type !== "image/jpeg")) {
        return openAlert('Os arquivos devem ser do formato PDF, PNG ou JPEG.', 'error');
      }
    }

    return setFiles(prev => ({ ...prev, [taxPayerId]: [...(!!prev[taxPayerId]?.length ? prev[taxPayerId] : []), ...uploadedFiles] }));
  };

  function handleRemoveFile (event: React.MouseEvent, taxPayerId: string) {
    event.stopPropagation();

    const filesCopy = { ...files };
    delete filesCopy[taxPayerId];

    setFiles({ ...filesCopy });

    const inputElement = document.getElementById(`${taxPayerId}-upload`) as HTMLInputElement;
    
    if (inputElement) {
      inputElement.value = "";
    }
  };


  return (
    <>
      {!isQueryFetched && isQueryFetching && <div>Loading...</div>}
      {!isQueryFetched && isQueryError && <div>Error...</div>}
      {people && (
        <>
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table>
              <TableHead>
                <TableRow style={{ height: '100px'}}>
                  <TableCell>CPF</TableCell>
                  <TableCell>Vinculado ao CPF</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Data de nascimento</TableCell>
                  <TableCell>Data de demissão</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Continuação do benefício</TableCell>
                  <TableCell>Upload arquivos</TableCell>
                  <TableCell>Data de exclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {people.map((person, index) => (
                  <TableRow key={person.taxPayerId} style={{ height: '100px' }}>
                    <Cell>{cpfMask(person.taxPayerId)}</Cell>
                    <Cell>{person.linkedTaxPayerId ? cpfMask(person.linkedTaxPayerId) : '-'}</Cell>
                    <Cell>{person.name}</Cell>
                    <Cell>{person.type === 'T' ? 'Titular' : 'Dependente'}</Cell>
                    <Cell>{formatDate(person.birthDate)}</Cell>
                    <Cell>{formatDate(person.dismissalDate)}</Cell>
                    <Cell>
                      <FormControl style={{ width: '250px' }}>
                        <Select
                          variant="standard"
                          value={person.chosenPlan ?? ""}
                          onChange={e => handleChangePlan(person.taxPayerId, e.target.value)}
                          disabled={isMutateSuccess}
                          style={{ padding: '10px 0px 10px 0px' }}
                        >
                          {person.plans.map(plan => (
                            <MenuItem key={plan.id} value={plan.name}>{plan.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Cell>
                    <Cell>
                      <FormControl style={{ width: '250px' }}>
                        <Select
                          variant="standard"
                          value={person.continueWithPlan ?? ""}
                          onChange={e => handleChangeOption(person.taxPayerId, e.target.value)}
                          disabled={isMutateSuccess}
                          style={{ padding: '10px 0px 10px 0px' }}
                        >
                          <MenuItem value='SIM'>Com plano de extensão do benefício</MenuItem>
                          <MenuItem value='NAO'>Sem plano de extensão do benefício</MenuItem>
                        </Select>
                        {!!errors[index]?.["continueWithPlan"] && <FormHelperText error>{errors[index]["continueWithPlan"]}</FormHelperText>}
                      </FormControl>
                    </Cell>
                    <Cell style={{ height: 'inherit', display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                      <Box sx={{ width: '210px' }}>
                        <input
                          accept="application/pdf,image/png,image/jpeg"
                          // disabled={!!files[person.taxPayerId]}
                          style={{ display: 'none' }}
                          id={`${person.taxPayerId}-upload`}
                          type="file"
                          multiple
                          onChange={(e) => handleFileChange(e, person.taxPayerId)}
                        />
                        <label htmlFor={`${person.taxPayerId}-upload`}>
                          <Button 
                            variant="outlined"
                            component="span"
                            // disabled={!!file}
                            sx={{
                              width: '100%',
                              height: '50px',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              paddingRight: 1,
                            }}
                          >
                            {!files[person.taxPayerId] && (
                              <UploadFile sx={{ marginRight: 1 }} fontSize="small" color="primary" />
                            )}
                            <Box
                              component="span"
                              sx={{
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                display: 'inline-block',
                                maxWidth: '100%',
                                flexGrow: 1,
                                marginRight: 1,
                              }}
                            >
                              {!!files[person.taxPayerId] ? `${files[person?.taxPayerId].length} arquivos selecionados` : 'Selecionar arquivo'}
                            </Box>
                          </Button>
                        </label>
                        {!!errors[index]?.["files"] && <FormHelperText error>{errors[index]["files"]}</FormHelperText>}
                      </Box>
                      <Box sx={{ width: '25px' }}>
                        {files[person.taxPayerId] && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleRemoveFile(e, person.taxPayerId)}
                            sx={{ marginLeft: 'auto', color: 'black' }}
                          >
                            <DeleteOutlined fontSize="small" color="error" />
                          </IconButton>
                        )}
                      </Box>
                    </Cell>
                    <Cell>
                      <TextField
                        type='date'
                        variant='filled'
                        margin='normal'
                        // disabled={!person.chosenPlan || person.chosenPlan === 'NONE' || isMutateSuccess}
                        disabled={isMutateSuccess}
                        value={person.selectedDate}
                        onChange={(e) => handleChangeDate(person.taxPayerId, e.target.value)}
                      />
                      {!!errors[index]?.["selectedDate"] && <FormHelperText error>{errors[index]["selectedDate"]}</FormHelperText>}
                    </Cell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <ButtonContainer>
              <Button
                variant='text'
                color="error"
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isMutatePending || isMutateSuccess || !(people.find(person => !!person.continueWithPlan))}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                variant='contained'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                // disabled={isMutatePending || isMutateSuccess || !(people.find(person => !!person.continueWithPlan)) || !(people.find(person => !!person.chosenPlan)) || !(people.find(person => !!person.selectedDate))}
                onClick={() => mutate(people)}
              >
                {isMutatePending ? <CircularProgress color='info' size={20}/> : 'Salvar'}
              </Button>
          </ButtonContainer>
        </>
      )}
    </>
  );
}
